// Author: Ahsan Saleem
/**
 * Control Centre and Notification Centre — the OS system rituals. The status
 * cluster opens operating mode, facility source, simulation, sync and health;
 * the bell opens operational activity. Every indicator is actionable, not decor.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import {
  Wifi, WifiOff, RefreshCw, FlaskConical, Bell, Gauge, Moon, Sun, Radio,
  Clock, TriangleAlert, CircleCheck, CircleAlert, ChevronRight,
} from 'lucide-react';
import { flush } from '../outbox.js';
import { siteSource } from '../api.js';
import { effectiveTheme, setTheme } from './theme.js';
import { useSystem } from './system.js';

/** Friendly label for a site's telemetry provenance (SENSOR wins over simulated). */
function sourceLabel(source: string | null): string {
  switch (source) {
    case 'SENSOR': return 'Sensor';
    case 'IMPORTED': return 'Imported';
    case 'MANUAL': return 'Manual';
    case 'SIMULATED': return 'Simulated';
    default: return 'Awaiting data';
  }
}

function freshnessOf(snap: ReturnType<typeof useSystem>['snapshot']) {
  if (!snap) return { stale: 0, total: 0 };
  const stale = snap.latest.filter((l) => l.stale).length;
  return { stale, total: snap.latest.length };
}

export function ControlCentre() {
  const { online, sync, snapshot, bloomState } = useSystem();
  const theme = effectiveTheme();
  const fresh = freshnessOf(snapshot);
  const source = snapshot ? siteSource(snapshot.latest) : null;
  const SourceIcon = source === 'SIMULATED' ? FlaskConical : Radio;
  const syncLabel = !online ? 'Offline' : sync.failed ? `${sync.failed} failed` : sync.pending ? `${sync.pending} queued` : 'Synced';
  const syncTone = !online ? 'offline' : sync.failed ? 'critical' : sync.pending ? 'caution' : 'success';

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button className={`sys-cluster tone-${syncTone}`} aria-label="Control centre">
          {online ? (sync.pending || sync.failed ? <RefreshCw size={13} /> : <Wifi size={13} />) : <WifiOff size={13} />}
          <span className="sys-cluster-l">{syncLabel}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="centre" sideOffset={8} align="end">
          <div className="centre-title">Control Centre</div>
          <div className="cc-grid">
            <button className="cc-tile" onClick={() => setTheme(theme === 'command' ? 'field' : 'command')}>
              {theme === 'command' ? <Moon size={17} /> : <Sun size={17} />}
              <span className="cc-tile-l">Mode</span><span className="cc-tile-v">{theme === 'command' ? 'Command' : 'Field'}</span>
            </button>
            <div className={`cc-tile${source === 'SIMULATED' ? ' is-sim' : ''}`}>
              <SourceIcon size={17} /><span className="cc-tile-l">Source</span><span className="cc-tile-v">{sourceLabel(source)}</span>
            </div>
            <button className={`cc-tile tone-${syncTone}`} onClick={() => void flush()} disabled={!online}>
              <RefreshCw size={17} /><span className="cc-tile-l">Sync</span><span className="cc-tile-v">{syncLabel}</span>
            </button>
            <div className={`cc-tile ${fresh.stale ? 'tone-caution' : 'tone-success'}`}>
              <Radio size={17} /><span className="cc-tile-l">Data</span>
              <span className="cc-tile-v">{fresh.stale ? `${fresh.stale} stale` : 'All fresh'}</span>
            </div>
          </div>
          <div className="cc-health">
            <Gauge size={14} aria-hidden />
            <span>Facility {bloomState === 'healthy' ? 'within limits' : bloomState === 'offline' ? 'offline' : 'needs attention'}</span>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function NotificationCentre() {
  const { snapshot, sync } = useSystem();
  const navigate = useNavigate();

  const items = useMemo(() => {
    const out: Array<{ id: string; tone: string; icon: typeof Bell; title: string; sub: string; to: string }> = [];
    if (snapshot) {
      for (const a of snapshot.alerts.slice(0, 6)) {
        out.push({
          id: 'al' + a.id, tone: a.severity === 'CRITICAL' ? 'critical' : a.state === 'RECOVERED' ? 'success' : 'caution',
          icon: a.severity === 'CRITICAL' ? CircleAlert : a.state === 'RECOVERED' ? CircleCheck : TriangleAlert,
          title: a.rule_name, sub: `${a.metric} · ${a.state.toLowerCase()}`, to: '/alerts',
        });
      }
      const overdue = snapshot.brief.attention.overdueTasks;
      if (overdue) out.push({ id: 'ov', tone: 'caution', icon: Clock, title: `${overdue} overdue task${overdue > 1 ? 's' : ''}`, sub: 'past their due time', to: '/today' });
    }
    if (sync.failed) out.push({ id: 'sf', tone: 'critical', icon: RefreshCw, title: `${sync.failed} failed to sync`, sub: 'retry from the queue', to: '/today' });
    return out;
  }, [snapshot, sync]);

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button className="bell" aria-label={`Notifications: ${items.length}`}>
          <Bell size={16} />
          {items.length > 0 && <span className="bell-dot" aria-hidden>{items.length}</span>}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="centre notif" sideOffset={8} align="end">
          <div className="centre-title">Activity</div>
          {items.length === 0 ? (
            <div className="notif-clear"><CircleCheck size={22} /><span>Nothing needs you right now</span></div>
          ) : (
            <div className="notif-list">
              {items.map((n) => {
                const Icon = n.icon;
                return (
                  <button key={n.id} className={`notif-row tone-${n.tone}`} onClick={() => navigate(n.to)}>
                    <span className="notif-ic"><Icon size={16} /></span>
                    <span className="notif-main"><span className="notif-t">{n.title}</span><span className="notif-s">{n.sub}</span></span>
                    <ChevronRight size={15} className="notif-go" aria-hidden />
                  </button>
                );
              })}
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
